/**
 * Cart drawer + checkout page for the SociBaba marketplace.
 *
 * The drawer lives on every /shop page (mounted in Shop.tsx <ShopShell>).
 * Checkout is a dedicated /shop/checkout page.
 *
 * Auth-required for M1 — guest cart is Phase M-guest.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Minus, Plus, ShoppingBag, Trash2, X, CheckCircle2, FileDown, Sparkles, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { ecom } from "@/lib/analytics";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { productImage } from "@/lib/productImage";
import { useCurrency } from "@/contexts/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface CartItemT {
  product_id: string; slug: string; title: string; brand?: string;
  image_url?: string; price: number; currency: string; quantity: number;
  variant_id?: string; variant_name?: string;
}
interface CartT {
  cart_id: string;
  items: CartItemT[];
}

export const useCart = () => useQuery<CartT>({
  queryKey: ["marketplace-cart"],
  queryFn: async () => (await api.get("/marketplace/cart")).data,
  retry: false,
});

export const CartDrawer: React.FC<{ open: boolean; onOpenChange: (o: boolean) => void }> = ({ open, onOpenChange }) => {
  const cart = useCart();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { formatPrice, code: displayCode } = useCurrency();

  const updateQty = useMutation({
    mutationFn: async (payload: { product_id: string; variant_id?: string; quantity: number }) =>
      (await api.patch("/marketplace/cart/items", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketplace-cart"] }),
  });
  const removeItem = useMutation({
    mutationFn: async (payload: { product_id: string; variant_id?: string }) =>
      (await api.delete(`/marketplace/cart/items/${payload.product_id}`, { params: payload.variant_id ? { variant_id: payload.variant_id } : {} })).data,
    onSuccess: (_, v) => {
      ecom.removeFromCart({ item_id: v.product_id, item_name: "" });
      qc.invalidateQueries({ queryKey: ["marketplace-cart"] });
    },
  });

  const items = cart.data?.items || [];
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" /> Your cart
          </SheetTitle>
          <SheetDescription>{items.length === 0 ? "Empty" : `${items.length} item${items.length > 1 ? "s" : ""}`}</SheetDescription>
        </SheetHeader>
        {cart.isLoading ? (
          <div className="mt-6 space-y-3">
            {[0,1,2].map(i => <div key={i} className="sg-skeleton h-20" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-10 text-center flex-1">
            <ShoppingBag className="h-10 w-10 mx-auto text-[color:var(--sg-muted)]" strokeWidth={1.5} />
            <div className="mt-3 text-sm text-[color:var(--sg-muted)]">Your cart is empty.</div>
            <Link to="/shop" onClick={() => onOpenChange(false)} className="mt-4 inline-block text-[color:var(--sb-saffron)] hover:underline text-sm">Browse Shop</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto mt-4 space-y-3">
              {items.map((i) => (
                <div key={i.product_id + (i.variant_id || "")} className="flex gap-3 p-3 rounded-lg border border-[color:var(--sg-border)]" data-testid={`cart-item-${i.slug}`}>
                  <div className="h-16 w-16 bg-[color:var(--sg-muted-bg)] rounded-md overflow-hidden shrink-0">
                    <img src={productImage({ image_url: i.image_url, slug: i.slug, product_id: i.product_id, title: i.title }, 200)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-[color:var(--sg-muted)]">{i.brand}</div>
                    <div className="font-semibold text-sm truncate">{i.title}</div>
                    {i.variant_name && <div className="text-xs text-[color:var(--sg-muted)]">{i.variant_name}</div>}
                    <div className="mt-1 flex items-center gap-2">
                      <div className="flex items-center border border-[color:var(--sg-border)] rounded-md">
                        <button onClick={() => updateQty.mutate({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity - 1 })} className="p-1 hover:bg-[color:var(--sg-muted-bg)]" data-testid={`cart-qty-minus-${i.slug}`}><Minus className="h-3 w-3" /></button>
                        <span className="px-2 text-sm font-semibold">{i.quantity}</span>
                        <button onClick={() => updateQty.mutate({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity + 1 })} className="p-1 hover:bg-[color:var(--sg-muted-bg)]" data-testid={`cart-qty-plus-${i.slug}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <div className="ml-auto font-display font-bold text-sm">{formatPrice(i.price * i.quantity)}</div>
                      <button onClick={() => removeItem.mutate({ product_id: i.product_id, variant_id: i.variant_id })} className="text-[color:var(--sg-muted)] hover:text-red-500" data-testid={`cart-remove-${i.slug}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-[color:var(--sg-border)] pt-4 mt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[color:var(--sg-muted)]">Subtotal</span>
                <span className="font-semibold">{formatPrice(subtotal)}</span>
              </div>
              <div className="text-[10px] text-[color:var(--sg-muted)]">GST + shipping calculated at checkout.</div>
              {displayCode !== "INR" && (
                <div className="text-[10px] text-[color:var(--sg-muted)] flex items-center gap-1" data-testid="cart-billed-in-inr-notice">
                  <Sparkles className="h-3 w-3 text-[color:var(--sb-saffron)]" />
                  Prices shown in {displayCode}. Checkout is billed in INR (₹).
                </div>
              )}
              <Button
                onClick={() => { onOpenChange(false); nav("/shop/checkout"); }}
                className="w-full bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white"
                data-testid="cart-checkout-btn"
              >
                Proceed to checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ------------------------ Add to cart button ------------------------
export const AddToCartButton: React.FC<{ productId: string; title: string; brand?: string; category?: string; price?: number; className?: string }> = ({ productId, title, brand, category, price, className }) => {
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: async () => (await api.post("/marketplace/cart/items", { product_id: productId, quantity: 1 })).data,
    onSuccess: () => {
      toast.success(`Added to cart: ${title}`);
      ecom.addToCart({ item_id: productId, item_name: title, item_brand: brand, item_category: category, price, quantity: 1 });
      qc.invalidateQueries({ queryKey: ["marketplace-cart"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Please log in to add to cart"),
  });
  return (
    <Button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); add.mutate(); }}
      disabled={add.isPending}
      className={`bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white ${className || ""}`}
      data-testid={`add-to-cart-${productId}`}
    >
      {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShoppingBag className="h-4 w-4 mr-1.5" /> Add to cart</>}
    </Button>
  );
};

// ------------------------ Checkout page ------------------------
export const ShopCheckout: React.FC = () => {
  const cart = useCart();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { formatPrice, code: displayCode } = useCurrency();
  const [addr, setAddr] = useState({ name: "", phone: "", email: "", line1: "", line2: "", city: "", state: "", postal_code: "" });
  const cfg = useQuery({ queryKey: ["mp-config"], queryFn: async () => (await api.get("/marketplace/config")).data });

  useEffect(() => {
    if (cart.data?.items?.length) {
      ecom.beginCheckout(cart.data.items.map(i => ({ item_id: i.product_id, item_name: i.title, item_brand: i.brand, price: i.price, quantity: i.quantity })), cart.data.items.reduce((s, i) => s + i.price * i.quantity, 0));
    }
  }, [cart.data]);

  const place = useMutation({
    mutationFn: async () => {
      const isForeignCurrency = displayCode !== "INR";

      // Foreign currency → Stripe Checkout Session redirect flow. We never
      // open the Razorpay modal because Razorpay only clears INR.
      if (isForeignCurrency) {
        const { data } = await api.post("/marketplace/checkout-stripe", {
          cart_id: cart.data!.cart_id,
          shipping_address: addr,
          currency: displayCode,
          origin_url: window.location.origin,
        });
        const stripe = (data as any).stripe;
        if (!stripe?.checkout_url) throw new Error("Stripe session missing checkout URL");
        // Full-page redirect — Stripe hosts the checkout UI.
        window.location.assign(stripe.checkout_url);
        // The mutation "succeeds" in the sense that we've handed off to Stripe;
        // the confirmation page will pick up the order after redirect.
        return { redirected: true };
      }

      // INR path — original Razorpay modal flow.
      const { data } = await api.post("/marketplace/checkout", {
        cart_id: cart.data!.cart_id,
        shipping_address: addr,
      });
      const { order, razorpay } = data as any;
      const resp = await openRazorpayCheckout({
        order_id: razorpay.order_id,
        amount: razorpay.amount,
        currency: "INR",
        key: razorpay.key_id,
        name: "SociBaba",
        description: `Order ${order.order_number}`,
        prefill: { email: addr.email, name: addr.name, contact: addr.phone },
      });
      if (!resp) throw new Error("Payment canceled");
      const verified = await api.post(
        `/marketplace/orders/${order.order_id}/verify`,
        resp,
      );
      return verified.data;
    },
    onSuccess: (result: any) => {
      // Stripe path already redirected — nothing else to do here.
      if (result?.redirected) return;
      const order = result;
      ecom.purchase(
        order.order_number,
        order.items.map((i: any) => ({ item_id: i.product_id, item_name: i.title, item_brand: i.brand, price: i.unit_price, quantity: i.quantity })),
        order.total,
        { tax: order.tax, currency: order.currency },
      );
      qc.invalidateQueries({ queryKey: ["marketplace-cart"] });
      nav(`/shop/order/${order.order_id}`);
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.detail || e?.message || "Checkout failed";
      if (msg === "Payment canceled") {
        toast.info("Payment canceled — you can try again anytime.");
      } else {
        toast.error(msg);
      }
    },
  });

  if (cart.isLoading) return <div className="max-w-3xl mx-auto p-12"><div className="sg-skeleton h-64" /></div>;
  if (!cart.data?.items?.length) {
    return <div className="max-w-3xl mx-auto p-12 text-center">
      <div className="font-display text-2xl">Your cart is empty.</div>
      <Link to="/shop" className="text-[color:var(--sb-saffron)] hover:underline mt-3 inline-block">Continue shopping</Link>
    </div>;
  }

  const subtotal = cart.data.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax = Math.round(subtotal * 0.18 * 100) / 100;
  const total = subtotal + tax;
  const canPlace = addr.name && addr.phone && addr.email && addr.line1 && addr.city && addr.state && addr.postal_code;

  return (
    <div className="max-w-5xl mx-auto p-6 sm:p-8" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-bold tracking-tight">Checkout</h1>
      <div className="mt-8 grid md:grid-cols-[1fr_360px] gap-8">
        <section className="sg-card p-6">
          <h2 className="font-display text-xl font-bold mb-4">Shipping address</h2>
          <div className="grid grid-cols-2 gap-3">
            {(["name","phone","email","line1","city","state","postal_code"] as const).map((f) => (
              <div key={f} className={f === "line1" ? "col-span-2" : ""}>
                <Label className="capitalize text-xs">{f.replace("_", " ")}</Label>
                <Input value={(addr as any)[f]} onChange={(e) => setAddr({ ...addr, [f]: e.target.value })} data-testid={`checkout-${f}`} className="mt-1" />
              </div>
            ))}
          </div>
        </section>
        <aside className="sg-card p-6 h-fit">
          <h2 className="font-display text-lg font-bold mb-4">Order summary</h2>
          <div className="space-y-3 mb-4">
            {cart.data.items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="flex-1 truncate">{i.title} × {i.quantity}</span>
                <span className="font-semibold">{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-1 border-t border-[color:var(--sg-border)] pt-3 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
            <div className="flex justify-between"><span>GST (18%)</span><span>{formatPrice(tax)}</span></div>
            <div className="flex justify-between font-display font-bold text-lg pt-2 border-t border-[color:var(--sg-border)]"><span>Total</span><span>{formatPrice(total)}</span></div>
          </div>
          <Button onClick={() => place.mutate()} disabled={!canPlace || place.isPending} className="w-full mt-4 bg-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron-hover)] text-white" data-testid="place-order-btn">
            {place.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {displayCode === "INR" ? "Opening Razorpay…" : "Redirecting to Stripe…"}</>
            ) : (
              <>Pay {formatPrice(total)}</>
            )}
          </Button>
          {displayCode !== "INR" ? (
            <div className="text-[10px] text-[color:var(--sg-muted)] mt-3 text-center leading-relaxed" data-testid="checkout-stripe-notice">
              You&apos;ll be redirected to Stripe to pay in {displayCode}. Charged live at today&apos;s FX rate — no INR conversion on your card.
            </div>
          ) : null}
          <div className="text-[10px] text-[color:var(--sg-muted)] mt-2 text-center">
            {displayCode === "INR"
              ? "Secure payments via Razorpay · Route split enabled for verified sellers."
              : "Secure payments via Stripe · Cards, wallets and local rails."}
          </div>
        </aside>
      </div>
    </div>
  );
};

// ------------------------ Order pending (Stripe redirect return) ------------------------
/**
 * Landing page after Stripe hosted checkout redirect. Polls
 * `/marketplace/orders/by-session/:sessionId` every 2s so the shopper sees
 * the confirmation the moment the webhook (or fallback status call) flips
 * their order to `paid`. Times out after ~30s and surfaces a retry link.
 */
export const ShopOrderPending: React.FC = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("stripe_session_id") || "";
  const nav = useNavigate();
  const [attempts, setAttempts] = useState(0);
  const qc = useQueryClient();
  const { formatPrice } = useCurrency();

  const poll = useQuery<any>({
    queryKey: ["mp-stripe-poll", sessionId],
    queryFn: async () => (await api.get(`/marketplace/orders/by-session/${sessionId}`)).data,
    enabled: !!sessionId,
    refetchInterval: 2000,
    refetchIntervalInBackground: false,
    gcTime: 5 * 60_000,
  });

  useEffect(() => {
    setAttempts((n) => n + 1);
  }, [poll.data]);

  const order = poll.data?.order;
  const paid = order?.status === "paid";

  useEffect(() => {
    if (paid && order?.order_id) {
      qc.invalidateQueries({ queryKey: ["marketplace-cart"] });
      // Small delay so the shopper sees the "Payment confirmed" pulse.
      const t = setTimeout(() => nav(`/shop/order/${order.order_id}`), 900);
      return () => clearTimeout(t);
    }
  }, [paid, order?.order_id, nav, qc]);

  const stuck = attempts > 15 && !paid;

  return (
    <div className="max-w-lg mx-auto py-24 px-6 text-center" data-testid="shop-order-pending">
      <div className={`h-16 w-16 mx-auto rounded-full grid place-items-center mb-5 ${
        paid ? "bg-emerald-100" : "bg-[color:var(--sb-saffron)]/10"
      }`}>
        {paid
          ? <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          : <Loader2 className="h-8 w-8 text-[color:var(--sb-saffron)] animate-spin" />
        }
      </div>
      <h1 className="font-display font-bold text-2xl text-slate-900 dark:text-white mb-2">
        {paid ? "Payment confirmed" : "Confirming your payment…"}
      </h1>
      <p className="text-sm text-[color:var(--sg-muted)] leading-relaxed">
        {paid
          ? "Redirecting you to the order confirmation…"
          : order
            ? `Stripe is finalising order ${order.order_number}. This usually takes 2-5 seconds.`
            : "Fetching your order — one moment."}
      </p>
      {stuck && (
        <div className="mt-8 text-xs text-[color:var(--sg-muted)] space-y-2" data-testid="shop-order-pending-stuck">
          <p>Taking longer than expected? Your order should still be saved.</p>
          <div className="flex items-center justify-center gap-2">
            <Link to="/shop/cart" className="underline hover:text-[color:var(--sg-fg)]">Back to cart</Link>
            {order?.order_id && (
              <><span>·</span><Link to={`/shop/order/${order.order_id}`} className="underline hover:text-[color:var(--sg-fg)]">Check order</Link></>
            )}
          </div>
        </div>
      )}
      {order?.stripe_amount_display && order?.stripe_currency && !paid && (
        <div className="mt-6 text-[11px] text-[color:var(--sg-muted)] font-mono tabular-nums" data-testid="shop-order-pending-amount">
          {order.stripe_currency.toUpperCase()} {order.stripe_amount_display.toFixed(2)} · order {order.order_number}
        </div>
      )}
    </div>
  );
};

// ------------------------ Order confirmation ------------------------
export const ShopOrderConfirm: React.FC = () => {
  const orderId = window.location.pathname.split("/").pop() || "";
  const { formatPrice, code: displayCode } = useCurrency();
  const order = useQuery<any>({
    queryKey: ["mp-order", orderId],
    queryFn: async () => (await api.get(`/marketplace/orders/${orderId}`)).data,
    enabled: !!orderId,
  });

  const downloadInvoice = async () => {
    try {
      const resp = await api.get(`/marketplace/orders/${orderId}/invoice.pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SociBaba-Invoice-${order.data?.order_number || orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Invoice not available yet");
    }
  };

  if (order.isLoading) return <div className="max-w-2xl mx-auto p-12"><div className="sg-skeleton h-64" /></div>;
  if (!order.data) return <div className="max-w-2xl mx-auto p-12 text-center">Order not found. <Link to="/shop" className="text-[color:var(--sb-saffron)] hover:underline">Back to Shop</Link></div>;
  const o = order.data;
  const isPaid = ["paid", "packed", "shipped", "delivered", "completed"].includes(o.status);
  // VIP loyalty discount metadata (only present when Bronze/Silver/Gold auto-applied)
  const hasVipDiscount = !!(o.discount_amount && o.discount_amount > 0 && o.discount_tier);
  const tierMedal = o.discount_tier === "gold" ? "🥇" : o.discount_tier === "silver" ? "🥈" : "🥉";
  const tierLabel = o.discount_tier ? o.discount_tier.charAt(0).toUpperCase() + o.discount_tier.slice(1) : "";
  const tierPalette = o.discount_tier === "gold"
    ? { bg: "from-yellow-100 via-amber-100 to-yellow-50", border: "border-yellow-400", text: "text-yellow-800", accent: "from-yellow-500 to-amber-600" }
    : o.discount_tier === "silver"
    ? { bg: "from-slate-100 via-zinc-100 to-slate-50", border: "border-slate-300", text: "text-slate-700", accent: "from-slate-400 to-zinc-600" }
    : { bg: "from-amber-50 via-orange-100 to-amber-100", border: "border-amber-400", text: "text-amber-800", accent: "from-amber-500 to-orange-700" };
  return (
    <div className="max-w-2xl mx-auto p-6 sm:p-12 text-center" data-testid="order-confirmation">
      <div className="h-14 w-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight mt-4">Order placed!</h1>
      <div className="text-sm text-[color:var(--sg-muted)] mt-2">Order <strong>{o.order_number}</strong> · {formatPrice(o.total)}</div>

      {/* VIP Loyalty discount celebration — only when a Bronze/Silver/Gold perk auto-applied */}
      {hasVipDiscount && (
        <div
          data-testid="order-vip-savings"
          data-tier={o.discount_tier}
          className={`mt-6 relative overflow-hidden rounded-2xl border-2 ${tierPalette.border} bg-gradient-to-r ${tierPalette.bg} p-5 text-left shadow-md`}
        >
          <div className="absolute -top-6 -right-6 h-24 w-24 rounded-full bg-white/40 blur-2xl pointer-events-none" />
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-full bg-gradient-to-br ${tierPalette.accent} grid place-items-center text-white shrink-0 shadow-lg text-2xl`}>
              <span aria-hidden>{tierMedal}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[10px] uppercase tracking-widest ${tierPalette.text} font-bold mb-0.5 inline-flex items-center gap-1.5`}>
                <Sparkles className="h-3 w-3" />
                {tierLabel} VIP discount applied
              </div>
              <div className="font-serif-display text-2xl sm:text-3xl font-bold text-neutral-900 leading-tight" data-testid="order-vip-savings-amount">
                You saved {formatPrice(o.discount_amount)}
                {o.discount_pct ? <span className={`ml-2 text-sm font-semibold ${tierPalette.text}`}>({o.discount_pct}% off)</span> : null}
              </div>
              <div className="text-sm text-neutral-700 mt-1">
                Your loyalty on the seller's AI Cart™ unlocked this exclusive rate — no code entry needed, it applied automatically.
              </div>
              {o.discount_code && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <code
                    data-testid="order-vip-code"
                    className={`inline-block rounded-lg border-2 ${tierPalette.border} bg-white px-2.5 py-1 font-mono text-xs sm:text-sm font-bold tracking-wider ${tierPalette.text} select-all`}
                  >
                    {o.discount_code}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(o.discount_code); toast.success(`Code ${o.discount_code} copied`); }}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-600 hover:text-neutral-900"
                    title="Copy code"
                  >
                    <Copy className="h-3 w-3" /> Copy
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 sg-card p-6 text-left">
        <div className="sg-label mb-3">Items</div>
        {o.items.map((i: any) => (
          <div key={i.product_id} className="flex justify-between text-sm py-2 border-b last:border-0 border-[color:var(--sg-border)]">
            <span>{i.title} × {i.quantity}</span>
            <span className="font-semibold">{formatPrice(i.line_total)}</span>
          </div>
        ))}

        {/* Totals breakdown — always shown, VIP row appears only if a discount is present */}
        <div className="mt-5 pt-4 border-t border-[color:var(--sg-border)] space-y-1.5" data-testid="order-totals-breakdown">
          <div className="flex justify-between text-sm">
            <span className="text-[color:var(--sg-muted)]">Subtotal</span>
            <span className="tabular-nums">{formatPrice(Number(o.subtotal || 0))}</span>
          </div>
          {hasVipDiscount && (
            <div
              className="flex justify-between text-sm font-semibold"
              data-testid="order-totals-vip-line"
            >
              <span className={`inline-flex items-center gap-1.5 ${tierPalette.text}`}>
                <span aria-hidden>{tierMedal}</span>
                {tierLabel} VIP discount
                {o.discount_pct ? <span className="opacity-70">· {o.discount_pct}% off</span> : null}
              </span>
              <span className={`tabular-nums ${tierPalette.text}`}>
                −{formatPrice(Number(o.discount_amount || 0))}
              </span>
            </div>
          )}
          {Number(o.bundle_discount_amount || 0) > 0 && (
            <div
              className="flex justify-between text-sm font-semibold text-emerald-600 dark:text-emerald-400"
              data-testid="order-totals-bundle-line"
            >
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden>✨</span>
                Buy Together bundle
                {o.bundle_discount_pct ? <span className="opacity-70">· {o.bundle_discount_pct}% off</span> : null}
                {o.bundle_group_count ? <span className="opacity-70">· {o.bundle_group_count} items</span> : null}
              </span>
              <span className="tabular-nums">
                −{formatPrice(Number(o.bundle_discount_amount || 0))}
              </span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-[color:var(--sg-muted)]">Shipping</span>
            <span className="tabular-nums">
              {Number(o.shipping || 0) === 0 ? "Free" : formatPrice(Number(o.shipping))}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-[color:var(--sg-muted)]">Tax (GST 18%)</span>
            <span className="tabular-nums">{formatPrice(Number(o.tax || 0))}</span>
          </div>
          <div className="flex justify-between font-serif-display font-bold text-lg pt-2 mt-1 border-t border-[color:var(--sg-border)]" data-testid="order-total-line">
            <span>Total</span>
            <span className="tabular-nums">{formatPrice(Number(o.total || 0))}</span>
          </div>
          {displayCode !== "INR" && (
            <div className="text-[10px] text-[color:var(--sg-muted)] pt-2 text-right">
              Displayed in {displayCode} — invoice billed ₹{Number(o.total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} in INR.
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-[color:var(--sg-muted)]">
          Shipping to <strong>{o.shipping_address.name}</strong> · {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
        </div>
      </div>
      {isPaid && (
        <Button
          onClick={downloadInvoice}
          variant="outline"
          className="mt-6 border-[color:var(--sb-saffron)] text-[color:var(--sb-saffron)] hover:bg-[color:var(--sb-saffron)]/5"
          data-testid="download-invoice-btn"
        >
          <FileDown className="h-4 w-4 mr-2" /> Download GST invoice (PDF)
        </Button>
      )}
      <div className="mt-6 space-x-3">
        <Link to="/shop" className="text-sm text-[color:var(--sb-saffron)] hover:underline">Continue shopping</Link>
      </div>
    </div>
  );
};

export default CartDrawer;
